import React from 'react';
import Stack from '../../Stack';
import SelectionCheckbox, { useRowSelection } from '../SelectionCheckbox';
import { GetCellStyleArgs } from '../../../../hooks/useTableCellDimensions';
import { Animated, LayoutChangeEvent, View } from 'react-native';
import { RichTableState, TableConfig } from '../types';
import { colors } from '../../../../utils/theme';
import { GetTableCellLayoutHandlerArgs } from '../../Table';
import Text from '../../Text';
import HoverDetectionContainer from '../../HoverDetectionContainer';
import PressableDynamicOpacity from '../../PressableDynamicOpacity';
import { useHoverState } from './useHoverState';
import { hexToRgbA } from '../../../../utils/hexToRGBA';
import { TABLE_PADDING } from '../utils';
import { useIsMobileLayout } from '../../../../hooks/useIsMobileLayout';

export type TableRowOwnProps<TDataElement> = {
    row: TDataElement;
    allData: TDataElement[];
    rowIndex: number;
    getTableCellLayoutHandler: (args: GetTableCellLayoutHandlerArgs) => (event: LayoutChangeEvent) => void;
    config: TableConfig<TDataElement>;
    state: RichTableState;
    onStateChange: (newState: RichTableState) => void;
    allowRowSelection: boolean;
    tableRowPadding: number;
    getCellStyle: (args: GetCellStyleArgs) => any;
};

export type TableRowProps<TDataElement> = TableRowOwnProps<TDataElement>;

export function TableRow<TDataElement>({
    row,
    allData,
    rowIndex,
    getTableCellLayoutHandler,
    config,
    state,
    onStateChange,
    allowRowSelection,
    tableRowPadding,
    getCellStyle,
}: TableRowProps<TDataElement>) {
    const isMobileLayout = useIsMobileLayout();
    const rowKey = config.getRowKey(row);
    const { onRowHover, animation, isHoverAnimating } = useHoverState({ rowKey });

    const { onChange } = useRowSelection({ onStateChange, state, allData, config, rowIndex, row, isHeader: false });

    const renderRowContent = (node: React.ReactNode) => {
        if (typeof node === 'string' || typeof node === 'number') {
            return <Text variant="table-content">{node}</Text>;
        } else {
            return node;
        }
    };

    const getRowElement = () => (
        <Stack
            horizontal
            fullWidth
            style={{
                backgroundColor: rowBackgroundColor,
                borderRadius: 5,
            }}
            testID={`rich-table-row-${rowIndex}`}
        >
            <SelectionCheckbox
                state={state}
                onStateChange={onStateChange}
                allowRowSelection={allowRowSelection}
                config={config}
                data={allData}
                isHeader={false}
                row={row}
                rowIndex={rowIndex}
            />
            {config.columns
                .filter((_) => !isMobileLayout || !_.hideOnMobile)
                .map((column, colIndex) => {
                    return (
                        <View
                            style={[
                                { padding: tableRowPadding },
                                getCellStyle({ colIndex, rowIndex }),
                                {
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'flex-start',
                                    flexDirection: 'column',
                                    alignSelf: 'stretch',
                                    width: 0,
                                },
                            ]}
                            onLayout={getTableCellLayoutHandler({
                                colIndex,
                                contentKey: String(column.getContentKey?.(row) || ''),
                                rowIndex,
                                isHeader: false,
                            })}
                            testID="table-cell"
                            key={`${column.label}-${colIndex}`}
                        >
                            {renderRowContent(column.render(row))}
                        </View>
                    );
                })}
        </Stack>
    );

    const rowBackgroundColor = rowIndex % 2 === 0 ? colors['White'] : hexToRgbA(colors['Grey-Light2'], 0.5);

    if (allowRowSelection) {
        return (
            <HoverDetectionContainer
                onHoverChange={onRowHover}
                style={{
                    width: '100%',
                    paddingLeft: TABLE_PADDING - 1,
                    paddingRight: TABLE_PADDING - 1,
                }}
            >
                <PressableDynamicOpacity onPress={onChange}>
                    <Animated.View
                        style={[
                            {
                                borderColor: isHoverAnimating
                                    ? animation.interpolate({
                                          inputRange: [0, 1],
                                          outputRange: [
                                              hexToRgbA(colors['Dividers'], 0),
                                              hexToRgbA(colors['Dividers'], 1),
                                          ],
                                      })
                                    : hexToRgbA(colors['Dividers'], 0),
                                borderWidth: 1,
                                borderRadius: 5,
                            },
                            { width: '100%' },
                        ]}
                    >
                        {getRowElement()}
                    </Animated.View>
                </PressableDynamicOpacity>
            </HoverDetectionContainer>
        );
    } else {
        return (
            <View style={{ width: '100%', paddingLeft: TABLE_PADDING, paddingRight: TABLE_PADDING }}>
                {getRowElement()}
            </View>
        );
    }
}
